import { usePrivy, useWallets } from "@privy-io/react-auth";
import { useAuth } from "contexts/authContext";
import { BigNumber, ethers } from "ethers";
import { ACCESS_TOKEN, deleteItem, getItem } from "local-storage";
import slugs from "navigation/slugs";
import { useCallback, useEffect, useState } from "react";
import { redirect } from "react-router";
import {
  getTiktokUserHoldingsById,
  getUserProfileData,
} from "services/apis/apis";
import { appConfig } from "services/blockchain";
import { formatAmount } from "utils/number/number";
import abi from "abis/Fantech.json";
import useSWR from "swr";

const provider = appConfig.provider;
const fantechCA = appConfig.fantechAddress;
const fanTechContract = new ethers.Contract(fantechCA, abi, provider);
const chainId = appConfig.chainId;
const defaultAmount = 1;

export const useController = () => {
  const { updateToken, handlelogout } = useAuth();
  const { authenticated, user, logout, sendTransaction } = usePrivy();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoading2, setIsLoading2] = useState(true);
  const [data, setData] = useState<any>({});
  const [currentTab, setCurrentTab] = useState("Stream");
  const [balance, setBalance] = useState("");
  const [poolSupply, setPoolSupply] = useState(0);

  const sub_address = data?.tiktok_user_info?.sub_address;

  const handleLogout = async () => {
    try {
      await logout();
      handlelogout();
    } catch (error) {
      console.error("An error occurred", error);
    }
  };

  useEffect(() => {
    if (!authenticated) {
      updateToken("");
      redirect(slugs.login);
    }
  }, [authenticated, updateToken]);

  const getUserProfile = useCallback(async () => {
    try {
      const accessToken = getItem(ACCESS_TOKEN) || "";
      if (!accessToken) return;
      const res = await getUserProfileData(accessToken);
      setData(res);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getUserProfile();
  }, [getUserProfile]);

  const { wallets } = useWallets();
  const embeddedWallet = wallets.find(
    (wallet) => wallet.walletClientType === "privy"
  );

  const walletAddress = embeddedWallet?.address;

  useEffect(() => {
    const getBalanceBase = async () => {
      if (!embeddedWallet) return;
      const balance = await appConfig.provider.getBalance(
        embeddedWallet?.address
      );
      setBalance(formatAmount(balance ?? "0", 18, 4));
    };
    getBalanceBase();
  }, [embeddedWallet]);

  useEffect(() => {
    if (!sub_address) {
      return setIsLoading2(false);
    }
    const f = async () => {
      try {
        setIsLoading2(true);

        const poolSupply: BigNumber = await fanTechContract.getPoolSupply(
          sub_address
        );
        setPoolSupply(poolSupply.toNumber());
      } catch (error) {
        console.log("🚀 ~ file: Controller.ts:115 ~ f ~ error:", error);
      } finally {
        setIsLoading2(false);
      }
    };
    f();
  }, [sub_address]);

  const handleBuyShare = async () => {
    try {
      const getPoolInInitialBuy = await fanTechContract.getPoolInInitialBuy(
        sub_address
      );

      let priceAfterFee;

      if (getPoolInInitialBuy) {
        priceAfterFee = await fanTechContract.getPoolInitialBuyPriceAfterFee(
          sub_address
        );
      } else {
        priceAfterFee = await fanTechContract.getBuyPriceAfterFee(
          sub_address,
          defaultAmount
        );
      }

      const uiConfig = {
        header: `Buy a ${data?.tiktok_user_info?.nickname || "Haska"} share`,
        description: `Buy 1 share for ${formatAmount(
          priceAfterFee,
          18,
          4
        )} ETH!`,
        buttonText: "Complete Purchase",
      };

      const abi = ["function buyShares(address sharesSubject)"];
      const iface = new ethers.utils.Interface(abi);
      const dataSMC = iface.encodeFunctionData("buyShares", [sub_address]);

      const unsignedTx = {
        to: fantechCA,
        chainId,
        data: dataSMC,
        value: priceAfterFee.toHexString(),
      };
      await sendTransaction(unsignedTx, uiConfig);
      mutateAccountShares();
      mutatePrice();
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:84 ~ handleBuyShare ~ error:",
        error
      );
    }
  };

  const handleSellShare = async () => {
    let sellAfterFee = BigNumber.from(0);
    try {
      sellAfterFee = await fanTechContract.getSellPriceAfterFee(
        sub_address,
        defaultAmount
      );
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:163 ~ handleSellShare ~ error:",
        error
      );
      return;
    }

    const uiConfig = {
      header: `Sell 1 ${data?.tiktok_user_info?.nickname || "Haska"} share`,
      description: `You will receive ${formatAmount(sellAfterFee, 18, 4)} ETH!`,
      buttonText: "Complete Sale",
    };

    try {
      const abi = [
        "function sellShares(address sharesSubject, uint256 amount)",
      ];

      const iface = new ethers.utils.Interface(abi);
      const dataSMC = iface.encodeFunctionData("sellShares", [
        sub_address,
        defaultAmount,
      ]);

      const unsignedTx = {
        to: fantechCA,
        chainId,
        data: dataSMC,
      };

      await sendTransaction(unsignedTx, uiConfig);
      mutatePrice();
      mutateAccountShares();
    } catch (error) {
      console.log(
        "🚀 ~ file: OtherProfile.tsx:185 ~ handleSellShare ~ error:",
        error
      );
    }
  };

  const {
    data: accountShares,
    isLoading: isLoading3,
    mutate: mutateAccountShares,
  } = useSWR(
    ["accountShares", sub_address],

    async () => {
      const result: BigNumber = await fanTechContract.getAccountShares(
        sub_address,
        sub_address
      );
      return result.toNumber();
    },
    { shouldRetryOnError: false }
  );

  const { data: buyPrice = BigNumber.from(0), mutate: mutatePrice } = useSWR(
    ["price", sub_address],
    async () => {
      if (!sub_address) return;
      const defaultAmount = 1;
      const price = await fanTechContract.getBuyPriceAfterFee(
        sub_address,
        defaultAmount
      );
      return price;
    },
    { shouldRetryOnError: false }
  );

  const { data: sellPrice = BigNumber.from(0) } = useSWR(
    ["sellPrice", data?.sub_address],
    async () => {
      if (!sub_address) return;
      const price = await fanTechContract.getSellPriceAfterFee(
        sub_address,
        defaultAmount
      );
      return price;
    }
  );

  return {
    user,
    accountShares,
    handleLogout,
    data,
    currentTab,
    setCurrentTab,
    isLoading: isLoading || isLoading2 || isLoading3,
    balance,
    walletAddress,
    poolSupply,
    buyPrice,
    handleBuyShare,
    sellPrice,
    handleSellShare,
  };
};
